import { useContext } from 'react';

import { AutoPanelContext } from '../AutoPanelProvider';
import { AutoPanelContextType } from '../types';

/**
 * Hook to access the AutoLayout system commands and state.
 * Must be used within an AutoPanelLayout component.
 *
 * @returns {AutoPanelContextType} The auto layout context with panel management methods
 * @throws {Error} When used outside of AutoPanelLayout
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { setPanel, clearPanel } = useAutoLayout();
 *
 *   useEffect(() => {
 *     if (selectedFile) {
 *       setPanel(LayoutArea.RIGHT_SIDEBAR, <FilePreview file={selectedFile} />);
 *     } else {
 *       clearPanel(LayoutArea.RIGHT_SIDEBAR);
 *     }
 *   }, [selectedFile, setPanel, clearPanel]);
 *
 *   return <FileGrid />;
 * }
 * ```
 */
export function useAutoLayout(): AutoPanelContextType {
  const context = useContext(AutoPanelContext);

  if (!context) {
    throw new Error(
      'useAutoLayout must be used within an AutoPanelLayout component. ' + 'Make sure your component is wrapped with <AutoPanelLayout> or <AutoPanelProvider>.',
    );
  }

  return context;
}

/**
 * Optional hook to safely access AutoLayout context without throwing.
 * Returns null if not within an AutoPanelLayout.
 *
 * @returns {AutoPanelContextType | null} The auto layout context or null
 *
 * @example
 * ```tsx
 * function OptionalPanelComponent() {
 *   const autoLayout = useAutoLayoutOptional();
 *
 *   if (autoLayout) {
 *     // Can use panel commands
 *     autoLayout.setPanel(LayoutArea.RIGHT_SIDEBAR, content);
 *   }
 *
 *   return <div>Content works with or without AutoLayout</div>;
 * }
 * ```
 */
export function useAutoLayoutOptional(): AutoPanelContextType | null {
  return useContext(AutoPanelContext);
}
